/**
 * The one place VConnect formats dates and times for display. Every
 * feature should import from here rather than calling
 * .toLocaleDateString()/.toLocaleString() directly - those default to
 * the browser's own locale, which silently produces US-style
 * month/day ordering for some viewers and something else for others.
 * Explicit options here mean every user sees the same format
 * regardless of their browser or OS locale settings.
 *
 * Style: day-month-year (20 Sep 2026), matching UN-document and
 * Nigerian convention - never the raw ISO string, never US m/d/y.
 */

const DATE_OPTIONS: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' };
const DATE_TIME_OPTIONS: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' };
const TIME_OPTIONS: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' };
const LOCALE = 'en-GB'; // day-month-year ordering, matches UN document convention

/** Empty/null-safe: returns a dash for missing values rather than throwing or showing "Invalid Date". */
function safeFormat(value: string | Date | null | undefined, options: Intl.DateTimeFormatOptions): string {
  if (!value) return '-';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat(LOCALE, options).format(date);
}

/** "20 Sep 2026" - for a date with no meaningful time component (departure_date, expiry dates, etc.). */
export function formatDate(value: string | Date | null | undefined): string {
  return safeFormat(value, DATE_OPTIONS);
}

/** "20 Sep 2026, 11:00 PM" - for a genuine timestamp (created_at, issued_at, departure_datetime, etc.). */
export function formatDateTime(value: string | Date | null | undefined): string {
  return safeFormat(value, DATE_TIME_OPTIONS);
}

/** "11:00 PM" - for when only the time matters (e.g., alongside a date already shown elsewhere). */
export function formatTime(value: string | Date | null | undefined): string {
  return safeFormat(value, TIME_OPTIONS);
}
